// 오류 서명·정규화(D5b, docs/COMMUNITY-SUPPORT-PRD.md §5). 브라우저 수집기와 Worker가 같은 함수를 쓴다(서버는 클라이언트 서명을 믿지 않고 다시 계산).
// 순수 함수만(DOM·저장소 없음). 개인정보 가리기: 이메일·긴 숫자열·URL 쿼리를 지운다.

export type ErrorKind = 'error' | 'rejection' | 'webgl-lost';
export const ERROR_KINDS: readonly ErrorKind[] = ['error', 'rejection', 'webgl-lost'];
export const MESSAGE_MAX = 300;
export const FRAME_MAX = 200;
/** 오류 보고 본문 상한(바이트) */
export const REPORT_MAX_BYTES = 4096;

/** 오류 보고 본문(평면 문자열만). 서버 검증 규칙과 같다 */
export interface ErrorReport {
  kind: ErrorKind;
  message: string;
  /** 첫 스택 줄(없으면 '') */
  frame: string;
  /** 게임 버전(package.json) */
  version: string;
  /** 빌드 SHA 앞 7~12자(없으면 'dev') */
  build: string;
  /** 현재 장면(title·village·dungeon 등) */
  scene: string;
  /** 브라우저 계열(chrome·safari·firefox·samsung·inapp·other) */
  browser: string;
  /** OS 계열(android·ios·windows·mac·linux·other) */
  os: string;
  /** 화면 크기(예: '844x390') */
  screen: string;
}

const cut = (s: string, n: number): string => (s.length > n ? s.slice(0, n) : s);

/** 개인정보처럼 보이는 조각을 가린다(이메일·6자리 이상 숫자열·URL 쿼리/해시) */
export function scrub(text: string): string {
  return text
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')
    .replace(/(https?:\/\/[^\s?#'"()]+)[?#][^\s'")]*/g, '$1')
    .replace(/\d{6,}/g, '[n]');
}

/** 보일 메시지: 공백 정리 + 가리기 + 길이 상한 */
export function cleanMessage(message: unknown): string {
  const s = typeof message === 'string' ? message : String(message ?? '');
  return cut(scrub(s.replace(/\s+/g, ' ').trim()), MESSAGE_MAX);
}

/** 스택에서 첫 '위치' 줄을 고른다(메시지 줄 'TypeError: …'는 건너뛴다) */
export function firstFrame(stack: unknown): string {
  if (typeof stack !== 'string') return '';
  for (const raw of stack.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    // V8 "at fn (url:1:2)", 사파리·파이어폭스 "fn@url:1:2"
    if (/^at\s/.test(line) || /@\S*:\d+/.test(line)) return cut(scrub(line), FRAME_MAX);
  }
  return '';
}

/** 서명용 정규화: 호스트·쿼리·자산 해시·줄/열 제거, 숫자 → #, 소문자 */
export function normalizeForSignature(text: string): string {
  return text
    .replace(/https?:\/\/[^/\s)]+/g, '')
    .replace(/[?#][^\s)]*/g, '')
    .replace(/-[A-Za-z0-9_]{6,12}(\.m?js)/g, '$1')
    .replace(/:\d+(:\d+)?/g, '')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** FNV-1a 두 갈래(32비트×2) — 동기·의존성 없음. 16 hex */
function fnv64(text: string): string {
  let h1 = 0x811c9dc5, h2 = 0xcbf29ce4;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ ((c + 0x9e) & 0xffff), 0x01000193) >>> 0;
  }
  return h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0');
}

/** 서명 = 종류|정규화 메시지|정규화 첫 스택 줄 → 16 hex */
export function errorSignature(kind: string, message: string, frame: string): string {
  return fnv64(kind + '|' + normalizeForSignature(message) + '|' + normalizeForSignature(frame));
}

const FIELD = /^[\w.:/x -]{0,64}$/;
const KEYS: readonly (keyof ErrorReport)[] = ['kind', 'message', 'frame', 'version', 'build', 'scene', 'browser', 'os', 'screen'];
const SHORT: readonly (keyof ErrorReport)[] = ['version', 'build', 'scene', 'browser', 'os', 'screen'];

/** 서버·클라이언트 공통 검증: 알려진 키만, 문자열만, 길이 상한. 틀리면 null */
export function parseErrorReport(value: unknown): ErrorReport | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (Object.keys(v).some((k) => !(KEYS as readonly string[]).includes(k))) return null;
  if (!ERROR_KINDS.includes(v.kind as ErrorKind)) return null;
  for (const k of KEYS) if (typeof v[k] !== 'string') return null;
  for (const k of SHORT) if (!FIELD.test(v[k] as string)) return null;
  if ((v.message as string).length > 2000 || (v.frame as string).length > 1000) return null;
  return {
    kind: v.kind as ErrorKind,
    message: cleanMessage(v.message),
    frame: cut(scrub(String(v.frame).trim()), FRAME_MAX),
    version: v.version as string, build: v.build as string, scene: v.scene as string,
    browser: v.browser as string, os: v.os as string, screen: v.screen as string,
  };
}
